import React from 'react';
import { Link } from 'react-router-dom';
import { Reveal } from '../components/ui';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { CONTACT_EMAIL, mailto } from '../lib/site';

/** Overarching FairCorp Terms of Service. Counterparty is Brennan Faires
 * (FairCorp is his trade name, not a separate legal entity). Product-specific
 * terms control for their product in case of conflict. */
const Tos: React.FC = () => {
  return (
    <section className="page-shell page-section-major">
      <div className="page-inner">
      <Reveal dir="up">
        <Card className="shadow-none overflow-hidden">
          <CardContent className="p-8 sm:p-10">
            <p className="text-primary-400 text-xs font-medium tracking-[0.24em] uppercase mb-4">
              Terms of Service
            </p>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              FairCorp &mdash; Terms of Service
            </h1>
            <p className="text-secondary-500 text-sm mb-6">
              Effective date: October 5, 2026 &middot; Last updated: October 5, 2026
            </p>
            <div className="text-secondary-300 text-[0.95rem] leading-loose space-y-4">
              <p>
                These Terms of Service (&ldquo;Terms&rdquo;) are a legal agreement between you and{' '}
                <strong className="text-white">Brennan Faires</strong> covering your use of the
                FairCorp websites and services &mdash; including the FairCorp hub
                (fair-corp.com), the FairCorp hosting directory, the FairCorp ToolBox storefront,
                and the websites hosted on FairCorp infrastructure (together, the
                &ldquo;Sites&rdquo;), as well as software distributed through them. FairCorp is a
                trade name for software and services published by Brennan Faires, an individual,
                and is not a separate legal entity; Brennan Faires is the sole legal counterparty
                to these Terms. By accessing or using the Sites, you agree to be bound by these
                Terms and by our <Link to="/privacy" className="text-primary-400 hover:text-primary-300 underline underline-offset-4">Privacy Policy</Link>.
              </p>
              <h2 className="text-white text-lg font-bold pt-2">1. Product-specific terms</h2>
              <p>
                Individual products distributed through the Service may carry their own terms. Where
                you download or use such a product, its product-specific terms also apply, and they
                control over these Terms for that product in case of conflict.
              </p>
              <h2 className="text-white text-lg font-bold pt-2">2. Who may use the Service</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>You must be at least 13 years old, and old enough to form a binding contract in the jurisdiction where you reside.</li>
                <li>You must not use the Service in any country where doing so would violate applicable law, or in violation of U.S. export-control and sanctions laws.</li>
                <li>You may not use the Service to collect or process the data of other people without their authorization.</li>
              </ul>
              <h2 className="text-white text-lg font-bold pt-2">3. Accounts</h2>
              <p>
                Where an account is required, you agree to provide accurate information and to keep
                your credentials confidential. You are responsible for all activity under your
                account, and you may not share, sell, or transfer your account except as expressly
                permitted by Brennan Faires in writing.
              </p>
              <h2 className="text-white text-lg font-bold pt-2">4. Acceptable use</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>Do not access, scrape, or interfere with the Service, our infrastructure, or any other user&rsquo;s data in ways not authorized by these Terms.</li>
                <li>Do not probe, scan, or test the vulnerability of the Service or circumvent any access or security control.</li>
                <li>Do not transmit malware, phishing content, or unlawful material through the Service.</li>
                <li>Do not use another person&rsquo;s account or impersonate someone else.</li>
                <li>Do not use the Service in any way that violates applicable law.</li>
              </ul>
              <h2 className="text-white text-lg font-bold pt-2">5. Software license</h2>
              <p>
                Subject to these Terms (and any applicable product-specific terms), Brennan Faires
                grants you a personal, non-exclusive, non-transferable, revocable license to
                download, install, and use software made available to you through the Service for
                its intended purpose. You may not resell, redistribute, sublicense, rent, or lease
                that software, or remove or obscure any proprietary notice in it.
              </p>
              <h2 className="text-white text-lg font-bold pt-2">6. Hosted sites and third parties</h2>
              <p>
                Websites hosted on FairCorp infrastructure may be operated for third parties and may
                carry their own product terms and privacy notices; those notices govern your use of
                that site alongside these Terms. Third-party services linked from the Sites are
                governed by their own terms, which you are responsible for following.
              </p>
              <h2 className="text-white text-lg font-bold pt-2">7. Disclaimers</h2>
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE SERVICE IS PROVIDED
                &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE,&rdquo; WITHOUT WARRANTIES OF ANY KIND,
                WHETHER EXPRESS, IMPLIED, OR STATUTORY.
              </p>
              <h2 className="text-white text-lg font-bold pt-2">8. Limitation of liability</h2>
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT WILL BRENNAN FAIRES
                BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR
                PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO THESE TERMS OR THE SERVICE &mdash;
                EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. BRENNAN FAIRES&rsquo;S TOTAL
                AGGREGATE LIABILITY WILL NOT EXCEED THE GREATER OF (A) ONE HUNDRED U.S. DOLLARS
                (US$100) OR (B) THE AMOUNTS YOU PAID BRENNAN FAIRES FOR THE SERVICE IN THE TWELVE
                (12) MONTHS PRECEDING THE CLAIM.
              </p>
              <h2 className="text-white text-lg font-bold pt-2">9. Indemnification</h2>
              <p>
                To the maximum extent permitted by law, you agree to indemnify and hold harmless
                Brennan Faires from claims arising out of your use of the Service, your violation
                of these Terms, your violation of third-party rights, or your violation of
                applicable law.
              </p>
              <h2 className="text-white text-lg font-bold pt-2">10. Termination</h2>
              <p>
                You may stop using the Service at any time. We may suspend or terminate your access
                if we reasonably believe you violated these Terms, if continued operation would
                violate law or create risk, or as required by law.
              </p>
              <h2 className="text-white text-lg font-bold pt-2">11. Governing law and disputes</h2>
              <p>
                These Terms are governed by the laws of the State of Colorado, U.S.A. You and
                Brennan Faires agree to first attempt informal resolution. Unresolved disputes will
                be brought exclusively in the state or federal courts in Boulder County, Colorado.
                Claims must be filed within one (1) year after arising, except where consumer law
                provides otherwise.
              </p>
              <h2 className="text-white text-lg font-bold pt-2">12. Changes</h2>
              <p>
                We may revise these Terms, updating the &ldquo;Last updated&rdquo; date above, with
                reasonable notice of material changes. Continued use after changes take effect means
                you accept the revised Terms.
              </p>
              <h2 className="text-white text-lg font-bold pt-2">13. Contact</h2>
              <p>
                Questions? Contact Brennan Faires (operating as FairCorp) at{' '}
                <a href={mailto()} className="text-primary-400 hover:text-primary-300 underline underline-offset-4">
                  {CONTACT_EMAIL}
                </a>.
              </p>
            </div>
            <Separator className="my-6" />
            <Link to="/" className="inline-flex items-center gap-2 text-primary-400 hover:text-primary-300 font-medium">
              Back to Home
            </Link>
          </CardContent>
        </Card>
      </Reveal>
      </div>
    </section>
  );
};

export default Tos;
